import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

const WSOD_DEDUP_KEY = 'mapleos_wsod_dedup_v1';
const WSOD_DEDUP_WINDOW_MS = 10_000;
const PERSIST_KEY = 'mapleos_state_v1';

export type WsodIncident = {
  scope: 'os-window' | 'os-root';
  appId?: string;
  appName?: string;
  windowId?: string;
  errorMessage?: string;
  stack?: string;
  componentStack?: string;
  recoveryAction?: string;
};

function getPersistedWindowSummary() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as {
      windows?: Array<{ id?: string; appId?: string; title?: string; filePath?: string }>;
      nextZIndex?: number;
    };

    return {
      nextZIndex: parsed.nextZIndex ?? null,
      windowCount: parsed.windows?.length ?? 0,
      windows: (parsed.windows || []).slice(-10).map((window) => ({
        id: window.id,
        appId: window.appId,
        title: window.title,
        filePath: window.filePath,
      })),
    };
  } catch {
    return { parseError: true };
  }
}

function shouldDeduplicate(incident: WsodIncident) {
  try {
    const key = JSON.stringify({
      scope: incident.scope,
      appId: incident.appId,
      windowId: incident.windowId,
      errorMessage: incident.errorMessage,
    });
    const raw = sessionStorage.getItem(WSOD_DEDUP_KEY);
    const previous = raw ? (JSON.parse(raw) as { key?: string; timestamp?: number }) : null;

    if (
      previous?.key === key &&
      typeof previous.timestamp === 'number' &&
      Date.now() - previous.timestamp < WSOD_DEDUP_WINDOW_MS
    ) {
      return true;
    }

    sessionStorage.setItem(
      WSOD_DEDUP_KEY,
      JSON.stringify({
        key,
        timestamp: Date.now(),
      })
    );
    return false;
  } catch {
    return false;
  }
}

export async function reportWhiteScreenOfDeath(incident: WsodIncident) {
  if (typeof window === 'undefined') {
    return;
  }

  if (shouldDeduplicate(incident)) {
    return;
  }

  const incidentId = uuidv4();
  const messageTarget = incident.appName || incident.appId || 'unknown-app';

  try {
    await supabase.functions.invoke('log-client-events', {
      body: {
        sessionId: incidentId,
        logs: [
          {
            level: 'error',
            message: `[WSOD] ${incident.scope} ${messageTarget}`,
            meta: {
              incidentType: 'white_screen_of_death',
              incidentId,
              ...incident,
              href: window.location.href,
              path: window.location.pathname,
              hash: window.location.hash,
              isElectron: Boolean((window as any).electronAPI?.isElectron),
              userAgent: navigator.userAgent,
              persistedWindowSummary: getPersistedWindowSummary(),
            },
          },
        ],
      },
    });
  } catch (error) {
    console.warn('[WSOD] Failed to report incident', error);
  }
}
